/** My settings: how ONE reaches me (phone/browser alerts, email, quiet hours) and my Microsoft 365 connection. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { currentSubscription, disablePush, enablePush, pushSupported } from "../lib/push";
import { Card, ErrorNote, PageHeader } from "../components/ui";
import { M365Card } from "./personal";

interface Pref { pushEnabled: boolean; quietStart: string; quietEnd: string; emailEnabled: boolean; emailCategories: string[] }
interface Cfg { vapidPublicKey: string | null; emailAvailable: boolean; email: string | null; categories: string[]; preference: Pref; devices: number }

const LABEL: Record<string, string> = {
  approval: "Approvals waiting for me",
  escalation: "Escalations and overdue warnings",
  decision: "Decisions I need to make",
  mention: "When someone mentions me",
  task: "Tasks assigned to me",
  request: "Updates on my requests",
  waiting: "Things waiting on me",
  update: "Other updates",
};

function DeviceAlerts() {
  const [state, setState] = useState<"unknown" | "on" | "off" | "denied" | "unavailable">("unknown");
  useEffect(() => {
    if (!pushSupported()) return setState("unavailable");
    if (Notification.permission === "denied") return setState("denied");
    void currentSubscription().then((s) => setState(s ? "on" : "off"));
  }, []);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="text-sm">
        <div className="font-medium">Alerts on this device</div>
        <div className="muted text-xs">
          {state === "on" ? "On for this browser." : state === "denied" ? "Blocked in this browser's settings." : state === "unavailable" ? "This browser doesn't support alerts. On iPhone, add ONE to the Home Screen first." : "Off for this browser."}
        </div>
      </div>
      {state === "off" && <button className="btn btn-primary" onClick={() => void enablePush().then((r) => setState(r === "enabled" ? "on" : r))}>Turn on</button>}
      {state === "on" && <button className="btn" onClick={() => void disablePush().then(() => setState("off"))}>Turn off</button>}
    </div>
  );
}

export function SettingsPage() {
  const qc = useQueryClient();
  const cfg = useQuery({ queryKey: ["notify-config"], queryFn: () => api<Cfg>("/api/notify/config") });
  const [p, setP] = useState<Pref | null>(null);
  useEffect(() => {
    if (cfg.data && !p) setP({ ...cfg.data.preference, quietStart: cfg.data.preference.quietStart.slice(0, 5), quietEnd: cfg.data.preference.quietEnd.slice(0, 5) });
  }, [cfg.data, p]);
  const save = useMutation({
    mutationFn: (v: Pref) => api("/api/notify/preference", { method: "PUT", json: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notify-config"] }),
  });
  const set = (patch: Partial<Pref>) => setP((x) => (x ? { ...x, ...patch } : x));
  const toggleCat = (c: string) => set({ emailCategories: p!.emailCategories.includes(c) ? p!.emailCategories.filter((x) => x !== c) : [...p!.emailCategories, c] });

  return (
    <div className="space-y-4">
      <PageHeader title="My settings" subtitle="How ONE gets in touch with you." />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Notifications">
          {cfg.error && <ErrorNote error={cfg.error} />}
          {p && cfg.data && (
            <form
              className="space-y-5"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate(p);
              }}
            >
              <DeviceAlerts />
              <div className="line space-y-2 border-t pt-4">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={p.emailEnabled} onChange={(e) => set({ emailEnabled: e.target.checked })} />
                  Email me{cfg.data.email ? ` at ${cfg.data.email}` : ""}
                </label>
                {!cfg.data.emailAvailable && <p className="muted text-xs">Email from ONE is being switched on. Your choices are saved and apply as soon as it's live.</p>}
                <fieldset disabled={!p.emailEnabled} className="grid gap-1.5 pl-6 sm:grid-cols-2">
                  {cfg.data.categories.map((c) => (
                    <label key={c} className={`flex items-center gap-2 text-sm ${p.emailEnabled ? "" : "opacity-50"}`}>
                      <input type="checkbox" checked={p.emailCategories.includes(c)} onChange={() => toggleCat(c)} />
                      {LABEL[c] ?? c}
                    </label>
                  ))}
                </fieldset>
                <p className="muted pl-6 text-xs">Anything marked urgent is always emailed.</p>
              </div>
              <div className="line space-y-2 border-t pt-4">
                <div className="text-sm font-medium">Quiet hours</div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="muted">From</span>
                  <input type="time" className="input !w-28" value={p.quietStart} onChange={(e) => set({ quietStart: e.target.value })} />
                  <span className="muted">to</span>
                  <input type="time" className="input !w-28" value={p.quietEnd} onChange={(e) => set({ quietEnd: e.target.value })} />
                </div>
                <p className="muted text-xs">No alerts or emails in these hours, except approvals, escalations and anything urgent. Everything still waits in your Inbox.</p>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={p.pushEnabled} onChange={(e) => set({ pushEnabled: e.target.checked })} />
                  Send alerts to my devices
                </label>
              </div>
              <ErrorNote error={save.error} />
              <div className="flex items-center gap-3">
                <button className="btn btn-primary" disabled={save.isPending}>{save.isPending ? "Saving…" : "Save"}</button>
                {save.isSuccess && <span className="muted text-sm">Saved.</span>}
              </div>
            </form>
          )}
        </Card>
        <div className="space-y-4">
          <M365Card />
          <PhoneCaptureCard />
        </div>
      </div>
    </div>
  );
}

/** How to send things to ONE from a phone's Share menu (Android: installed app; iPhone: a one-time Shortcut). */
function PhoneCaptureCard() {
  const url = `${window.location.origin}/share?text=`;
  const [copied, setCopied] = useState(false);
  return (
    <Card title="Capture from your phone">
      <div className="space-y-3 text-sm">
        <p className="muted">Share an email, message or web page to ONE and turn it into tasks or a note.</p>
        <details open>
          <summary className="cursor-pointer font-semibold">iPhone — “Send to ONE” Shortcut (2 minutes, once)</summary>
          <ol className="mt-2 list-decimal space-y-1 pl-5">
            <li>Open the <b>Shortcuts</b> app → <b>+</b> (new shortcut) → name it <b>Send to ONE</b>.</li>
            <li>Tap the <b>ⓘ</b> details → turn on <b>Show in Share Sheet</b>.</li>
            <li>Add the action <b>URL Encode</b> (it uses the Shortcut Input).</li>
            <li>Add the action <b>Open URLs</b> and type this, then insert the <i>URL Encoded Text</i> variable at the end:
              <span className="mt-1 flex items-center gap-2"><code className="min-w-0 flex-1 truncate rounded bg-black/[0.05] px-2 py-1 text-xs">{url}</code>
                <button className="btn btn-sm shrink-0" onClick={() => { void navigator.clipboard?.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); }}>{copied ? "Copied" : "Copy"}</button></span>
            </li>
            <li>Done. In Mail, Messages, Safari or WhatsApp: <b>Share → Send to ONE</b>.</li>
          </ol>
        </details>
        <details>
          <summary className="cursor-pointer font-semibold">Android</summary>
          <ol className="mt-2 list-decimal space-y-1 pl-5">
            <li>Open ONE in Chrome → menu ⋮ → <b>Add to Home screen</b> (Install).</li>
            <li>From any app: <b>Share → IF ONE</b>.</li>
          </ol>
        </details>
        <p className="muted text-xs">Emails in Outlook on your phone can also use the ONE for Outlook add-in.</p>
      </div>
    </Card>
  );
}
